<template>
    <FormCard>
        <template #cardTitle>
            Diagnostic Procedure Request Form
        </template>
        <template #cardSubtitle>
            Please enter details about the patient's condition and required procedure.
        </template>
        <template #cardText>
            <v-card style="margin-top: 20px;">
                <v-card-text>
                    <v-radio-group
                        v-model="medico_legal"
                        column
                        :rules="[v=> v !== null || 'This question is required']"
                    >
                        <template #label>
                            <div style="font-family: sans-serif; font-size: 17px; color: black">
                                Is this diagnostic procedure for cases related to a vehicular accident or other medico-legal case?
                            </div>
                        </template>
                        <v-radio
                            color="teal darken-1"
                            label="Yes"
                            :value="true"
                        />
                        <v-radio
                            color="teal darken-1"
                            label="No"
                            :value="false"
                        />
                    </v-radio-group>
                    <div v-if="isMemberOfChildbearingAge">
                        <v-radio-group
                            v-model="membPregnantInfo.status"
                            color="teal"
                            style="margin: 0;"
                            dense
                            :rules="[v=> v !== null || 'Member\'s pregnancy status is required']"
                        >
                            <v-radio
                                label="Not Pregnant"
                                :value="false"
                                color="teal"
                            />
                            <v-radio
                                label="Pregnant"
                                :value="true"
                                color="teal"
                            />
                            <template #label>
                                <span style="font-size: 17px; color: black">Select the member's pregnancy status:</span>
                            </template>
                        </v-radio-group>
                        <v-text-field
                            v-model="membPregnantInfo.lmp"
                            type="date"
                            color="teal"
                            :rules="dateRules"
                        >
                            <template #label>
                                <span style="font-size: 17px;">Last Menstrual Period (LMP) Date:</span>
                            </template>
                        </v-text-field>
                    </div>
                </v-card-text>
            </v-card>
            <div style="margin-top: 10px;" @mouseenter="triggerError()">
                <v-textarea
                    v-model="reason"
                    :rules="rules.reason"
                    color="#009688"
                    clearable
                    label="Reason for Diagnostic Procedure / Chief Complaint"
                    auto-grow
                    rows="1"
                    row-height="15"
                    autocomplete="nope"
                />
                <InitialDiagnosis
                    :data="data.diagnosis"
                    :memb-pregnant-info="membPregnantInfo"
                    @saveInitialDiagnosis="saveInitialDiagnosis"
                />
            </div>
            <v-alert
                rounded="lg"
                color="#E0F2F1"
                type="info"
                variant="tonal"
                class="pa-4 noto-sans"
                :class="{ shake: isShaking }"
                style="color: #004D40; background-color: #E0F2F1; letter-spacing: .5px; margin-top: 5px; padding: 10px;"
                outlined
                :icon="mdiAlertCircleOutline"
            >
                <!-- <strong style="font-size: 20px;">IMPORTANT REMINDER REGARDING DIAGNOSIS</strong><br><br> -->
                <span style="font-size: 20px; display: inline-block; font-weight: 600; margin-bottom: 5px;">Important Reminder re: Diagnosis</span><br>
                To ensure the diagnostic accuracy and the tests are appropriately justified, <b>all basic laboratory tests should have a clear indication based on the above diagnosis.</b>
            </v-alert>
            <SelectDiagnostics
                :data="data"
                :trigger-change-color="triggerChangeColor"
                @saveDiagnostics="saveDiagnostics"
                @isAmountExceedLimitChange="isAmountExceedLimitFunc"
            />
        </template>
    </FormCard>
</template>

<script>
import SelectDiagnostics from './InputComp/SelectDiagnostics.vue'
import InitialDiagnosis from './InputComp/InitialDiagnosis.vue'
import FormCard from './InputComp/FormCard.vue'
import { moment } from '../../../../utils/date-shim'
import { mapGetters } from 'vuex'
import { mdiAlertCircleOutline } from '@mdi/js'
export default {
    components: {
        FormCard,
        SelectDiagnostics,
        InitialDiagnosis
    },
    props: {
        data: {
            type: Object,
            required: true
        },
        triggerChangeColor: {
            type: Number,
            default: 0
        }
    },
    data () {
        return {
            mdiAlertCircleOutline,
            isShaking: false,
            medico_legal: null,
            membPregnantInfo: {
                status: null,
                lmp: null
            },
            reason: null,
            rules: {
                reason: [
                    v => !!v || 'This field is required',
                    v => /[a-zA-Z]/.test(v) || 'Please provide a valid reason!'
                ]
            },
            dateRules: [v => !!v || 'This field is required',  v => !moment(v).utcOffset('+0800').startOf('day').isAfter(moment().utcOffset('+0800').startOf('day')) || 'Last Menstrual Period (LMP) Date cannot be in the future']
        }
    },
    computed: {
        ...mapGetters(['membDetails']),
        isMemberOfChildbearingAge() {
            const membInfo = this.membDetails && this.membDetails.data
            if (membInfo.SEX === 'M')
                return false
            const bdate12am = moment(membInfo.BIRT_D).utcOffset('+0800').startOf('day')
            const age = moment().utcOffset('+0800').startOf('day').diff(bdate12am, 'years')
            return age >= 14 && age <= 50
        }
    },
    watch: {
        medico_legal(newVal) {
            this.$emit('isVehicularAccident', newVal)
        },
        'membPregnantInfo.status'(newVal) {
            this.$emit('isMemberOfChildbearingAge', this.isMemberOfChildbearingAge)
            this.$emit('isMemberPregnant', newVal)
        },
        'membPregnantInfo.lmp'(newVal) {
            this.$emit('lmpDate', newVal)
        },
        reason(newVal) {
            this.$emit('setReason', newVal)
        }
    },
    methods: {
        triggerError() {
            this.isShaking = true
            setTimeout(() => {
                this.isShaking = false
            }, 500) // Remove class after animation
        },
        isAmountExceedLimitFunc(newVal) {
            this.$emit('isAmountExceedLimitChange', newVal)
        },
        saveInitialDiagnosis(newVal) {
            this.$emit('saveInitialDiagnosis', newVal)
        },
        /**
         * Prepares the correct label for Diagnostics based on filters.
         * @param {object} newVal Value from `./SelectDiagnostics.vue`
         */
        saveDiagnostics(newVal) {
            const diagnostics = structuredClone(newVal.diagnostics)
            const xray = diagnostics.findIndex(item => item.label === 'Chest X-RAY')
            if (diagnostics[xray].value)
                diagnostics[xray].label = newVal.options

            const cbc = diagnostics.findIndex(item => item.label === 'Complete Blood Count')
            if (diagnostics[cbc].value && newVal.isCbcWithPlatelets)
                diagnostics[cbc].label = 'Complete Blood Count - with Platelet Count'
            this.$emit('saveDiagnostics', diagnostics)
        }
    }
}
</script>

<style scoped>
/* @import url('https://fonts.googleapis.com/css2?family=Noto+Sans:ital,wght@0,100..900;1,100..900&display=swap'); */

.noto-sans {
  font-family: "Noto Sans", sans-serif;
  font-optical-sizing: auto;
  font-weight: 500;
  font-style: normal;
  font-variation-settings:
    "wdth" 100;
}

.shake {
  animation: shake 0.5s ease-in-out;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  20%, 60% { transform: translateX(-5px); }
  40%, 80% { transform: translateX(5px); }
}
</style>
